import React, { useState, useEffect } from "react";
import "./Rooms.css";
import RoomCarousel from "../../components/RoomsCarousel/RoomsCarousel";
import { IKImage } from "imagekitio-react";
import {
  room1Exports,
  room2Exports,
  room3Exports,
} from "../../exports/roomExports";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const Rooms = () => {
  const [activeRoom, setActiveRoom] = useState<number | null>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const urlEndpoint = "https://ik.imagekit.io/kimchel";

  const roomsData = [
    {
      id: 1,
      title: t("roomsPage.room1.title"),
      description: t("roomsPage.room1.description"),
      amenities: t("roomsPage.room1.amenities", { returnObjects: true }) as string[],
      images: room1Exports,
    },
    {
      id: 2,
      title: t("roomsPage.room2.title"),
      description: t("roomsPage.room2.description"),
      amenities: t("roomsPage.room2.amenities", { returnObjects: true }) as string[],
      images: room2Exports,
    },
    {
      id: 3,
      title: t("roomsPage.room3.title"),
      description: t("roomsPage.room3.description"),
      amenities: t("roomsPage.room3.amenities", { returnObjects: true }) as string[],
      images: room3Exports,
    },
  ];

  // Scroll to the room specified in the URL hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          150
        );
      }
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.hash]);

  return (
    <div className="roomsPage">
      {/* Page header */}
      <div className="roomsPageHeader">
        <h1 className="roomsPageTitle">{t("nav.rooms")}</h1>
      </div>

      {/* Room sections */}
      {roomsData.map((room, index) => (
        <section
          key={room.id}
          id={`room-${room.id}`}
          className={`roomSection${index % 2 !== 0 ? " image-right" : ""}`}
        >
          {/* Image side */}
          <div className="roomImageSide">
            <div
              className="roomMainImage"
              onClick={() => setActiveRoom(room.id)}
              role="button"
              aria-label={`View photos of ${room.title}`}
            >
              <IKImage
                urlEndpoint={urlEndpoint}
                path={room.images[0]}
                className="roomMainImg"
                alt={room.title}
              />
            </div>

            {/* Thumbnail strip — only if more than 1 image available */}
            {room.images.length > 1 && (
              <div className="roomThumbs">
                {room.images.slice(1, 4).map((img, i) => (
                  <div
                    key={i}
                    className="roomThumb"
                    onClick={() => setActiveRoom(room.id)}
                  >
                    <IKImage
                      urlEndpoint={urlEndpoint}
                      path={img}
                      className="roomThumbImg"
                      alt={`${room.title} ${i + 2}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Text side */}
          <div className="roomTextSide">
            <span className="roomNumber">0{room.id}</span>
            <h2 className="roomTitle">{room.title}</h2>
            <div className="roomDivider" />
            <p className="roomDescription">{room.description}</p>

            <ul className="roomAmenities">
              {room.amenities.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            <button
              className="roomViewBtn"
              onClick={() => setActiveRoom(room.id)}
            >
              {t("roomsPage.viewPhotos")} →
            </button>
          </div>
        </section>
      ))}

      {/* Lightbox carousel */}
      {activeRoom !== null && (
        <RoomCarousel
          images={roomsData.find((r) => r.id === activeRoom)?.images || []}
          onClose={() => setActiveRoom(null)}
        />
      )}
    </div>
  );
};

export default Rooms;
